import React, { useState } from "react";
import { useSelector } from "react-redux";
import { userRequest } from "../../requestMethods";
import { FiLock, FiEye, FiEyeOff, FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import "./changePassword.css";

const ChangePassword = () => {
    const currentUser = useSelector((state) => state.user.currentUser);
    const [inputs, setInputs] = useState({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
    });
    const [showPasswords, setShowPasswords] = useState({
        oldPassword: false,
        newPassword: false,
        confirmPassword: false,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const handleChange = (e) => {
        setInputs((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
        // Clear messages when user types
        setError("");
        setSuccess("");
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    const validatePassword = () => {
        if (!inputs.oldPassword || !inputs.newPassword || !inputs.confirmPassword) {
            setError("All fields are required");
            return false;
        }

        if (inputs.newPassword.length < 6) {
            setError("New password must be at least 6 characters long");
            return false;
        }

        if (inputs.newPassword !== inputs.confirmPassword) {
            setError("New password and confirm password do not match");
            return false;
        }

        if (inputs.oldPassword === inputs.newPassword) {
            setError("New password must be different from old password");
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!validatePassword()) {
            return;
        }

        setLoading(true);

        try {
            const response = await userRequest.put(`/users/changepassword/${currentUser._id}`, {
                oldPassword: inputs.oldPassword,
                newPassword: inputs.newPassword,
            });

            setSuccess("Password changed successfully!");
            setInputs({
                oldPassword: "",
                newPassword: "",
                confirmPassword: "",
            });
        } catch (err) {
            setError(err.response?.data?.message || "Failed to change password. Please check your old password and try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="change-password-container">
            <div className="change-password-wrapper">
                <div className="change-password-card">
                    {/* Header */}
                    <div className="change-password-header">
                        <div className="header-icon">
                            <FiLock className="lock-icon" />
                        </div>
                        <h1 className="header-title">Change Password</h1>
                        <p className="header-subtitle">
                            Update your password to keep your account secure
                        </p>
                    </div>

                    {/* Alert Messages */}
                    {error && (
                        <div className="alert alert-error">
                            <FiAlertCircle className="alert-icon" />
                            <span>{error}</span>
                        </div>
                    )}

                    {success && (
                        <div className="alert alert-success">
                            <FiCheckCircle className="alert-icon" />
                            <span>{success}</span>
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="change-password-form">
                        {/* Old Password */}
                        <div className="form-group">
                            <label className="form-label">
                                <FiLock className="label-icon" />
                                Old Password
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPasswords.oldPassword ? "text" : "password"}
                                    name="oldPassword"
                                    value={inputs.oldPassword}
                                    onChange={handleChange}
                                    placeholder="Enter your current password"
                                    className="form-input"
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    onClick={() => togglePasswordVisibility("oldPassword")}
                                    className="password-toggle"
                                    disabled={loading}
                                >
                                    {showPasswords.oldPassword ? (
                                        <FiEyeOff className="toggle-icon" />
                                    ) : (
                                        <FiEye className="toggle-icon" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* New Password */}
                        <div className="form-group">
                            <label className="form-label">
                                <FiLock className="label-icon" />
                                New Password
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPasswords.newPassword ? "text" : "password"}
                                    name="newPassword"
                                    value={inputs.newPassword}
                                    onChange={handleChange}
                                    placeholder="Enter your new password"
                                    className="form-input"
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    onClick={() => togglePasswordVisibility("newPassword")}
                                    className="password-toggle"
                                    disabled={loading}
                                >
                                    {showPasswords.newPassword ? (
                                        <FiEyeOff className="toggle-icon" />
                                    ) : (
                                        <FiEye className="toggle-icon" />
                                    )}
                                </button>
                            </div>
                            <p className="form-hint">Password must be at least 6 characters long</p>
                        </div>

                        {/* Confirm New Password */}
                        <div className="form-group">
                            <label className="form-label">
                                <FiLock className="label-icon" />
                                Confirm New Password
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPasswords.confirmPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    value={inputs.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="Re-enter your new password"
                                    className="form-input"
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    onClick={() => togglePasswordVisibility("confirmPassword")}
                                    className="password-toggle"
                                    disabled={loading}
                                >
                                    {showPasswords.confirmPassword ? (
                                        <FiEyeOff className="toggle-icon" />
                                    ) : (
                                        <FiEye className="toggle-icon" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            className="submit-button"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="loading-spinner"></span>
                                    Changing Password...
                                </>
                            ) : (
                                <>
                                    <FiLock className="button-icon" />
                                    Change Password
                                </>
                            )}
                        </button>
                    </form>

                    {/* User Info */}
                    <div className="user-info">
                        <p className="user-email">
                            Changing password for: <strong>{currentUser?.email}</strong>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;

